import { Injectable } from '@angular/core';
import { Consumption, DatedValue, Utility, UtilityDetail, UtilitySummary } from 'brain-data/model/utility.model';
import { SELECTABLE_UTILITIES, UTILITY_UNIT, findUtility } from 'constants/utility.constant';
import { NumberService } from 'shared/services/utils/number/number.service';

@Injectable({
  providedIn: 'root',
})
export class UtilityTransformService {
  constructor(private numberService: NumberService) {}

  // FILTER

  filterUtilitiesDetailByDateRange = (
    utilityDetails: Record<
      string,
      {
        usage: DatedValue[];
        spend: DatedValue[];
      }
    >,
  ): UtilityDetail[] =>
    Object.entries(utilityDetails).map(([key, value]) => ({
      [key]: {
        usage: value.usage,
        spend: value.spend,
      },
    }));

  filterUtilitiesToYearly = (utilitiesParam: Utility[]): Utility[] => {
    const utilities: Utility[] = [];

    for (const item of utilitiesParam) {
      const spendData = item?.consumption?.spend;
      const usageData = item?.consumption?.usage;

      const yearlySpendTotal: Record<string, number> = {};
      const yearlyUsageTotal: Record<string, number> = {};

      if (spendData) {
        for (const spend of spendData) {
          const year = spend.date?.split('-')[0];
          const value = spend.value;

          if (year !== undefined) {
            if (year in yearlySpendTotal) {
              yearlySpendTotal[year] += value ?? 0;
            } else {
              yearlySpendTotal[year] = value ?? 0;
            }
          }
        }
      }

      if (usageData) {
        for (const usage of usageData) {
          const year = usage.date?.split('-')[0];
          const value = usage.value;

          if (year !== undefined) {
            if (year in yearlyUsageTotal) {
              yearlyUsageTotal[year] += value ?? 0;
            } else {
              yearlyUsageTotal[year] = value ?? 0;
            }
          }
        }
      }

      const yearlySpendItems: DatedValue[] = [];
      const yearlyUsageItems: DatedValue[] = [];

      const sortedSpendYears = Object.keys(yearlySpendTotal).slice().sort();
      const sortedUsageYears = Object.keys(yearlyUsageTotal).slice().sort();

      for (const year of sortedSpendYears) {
        const spendValue = yearlySpendTotal[year];
        const spendItem: DatedValue = {
          value: spendValue,
          date: year,
        };
        yearlySpendItems.push(spendItem);
      }

      for (const year of sortedUsageYears) {
        const usageValue = yearlyUsageTotal[year];
        const usageItem: DatedValue = {
          value: usageValue,
          date: year,
        };
        yearlyUsageItems.push(usageItem);
      }

      const consumption: Consumption = {
        unit: item.consumption?.unit ?? '',
        value: 0,
        spend: yearlySpendItems,
        usage: yearlyUsageItems,
      };

      const yearlyUtility: Utility = {
        name: item.name,
        icon: item.icon,
        consumption,
      };

      utilities.push(yearlyUtility);
    }

    return utilities;
  };

  // CONVERTS

  /**
   * BRN-2282/2283 AC4: this was a chain of five near-identical `if` blocks, one per commodity, each
   * naming its own label, icon and unit. A sixth commodity would simply have been absent from every
   * summary with nothing to notice it. It walks the one commodity definition now, so every commodity
   * BRAIN Core types is listed, labelled and given its icon; the five original ones produce exactly
   * the rows they produced before (`Electricity`/`kWh`, `Water`/`Gallons`, `Gas`/`MMBtu`,
   * `Solar`/`kWh`, `Propane`/`Gallons`), which is what keeps AC6 true on this path.
   */
  convertUtilitySummariesToUtilities = (summary: UtilitySummary): Utility[] =>
    SELECTABLE_UTILITIES.filter(c => summary[c.key] !== undefined && summary[c.key] !== 0).map(c => ({
      name: c.label,
      icon: c.icon,
      consumption: {
        value: summary[c.key],
        unit: this.getUtilityUnit(c.key),
        spend: [],
        usage: [],
      },
    }));

  convertUtilityDetailsToUtilities = (utilityDetails: UtilityDetail[]): Utility[] => {
    const utilities: Utility[] = [];
    for (const utilityDetail of utilityDetails) {
      for (const key in utilityDetail) {
        if (Object.prototype.hasOwnProperty.call(utilityDetail, key)) {
          const utilityData = utilityDetail[key];
          const consumption: Consumption = {
            unit: this.getUtilityUnit(key),
            value: undefined,
            spend: [],
            usage: [],
          };

          for (const spendData of utilityData.spend) {
            const spend: DatedValue = {
              value: this.numberService.formatNumberWithTwoDecimalPlaces(spendData.value),
              date: spendData.date,
              dataSourcePath: spendData.dataSourcePath,
              ...(spendData.dataSourcePaths?.length ? { dataSourcePaths: spendData.dataSourcePaths } : {}),
              ...this.pickPointAnnotations(spendData),
            };
            consumption.spend.push(spend);
          }

          for (const usageData of utilityData.usage) {
            const usage: DatedValue = {
              value: this.numberService.formatNumberWithTwoDecimalPlaces(usageData.value),
              date: usageData.date,
              dataSourcePath: usageData.dataSourcePath,
              ...(usageData.dataSourcePaths?.length ? { dataSourcePaths: usageData.dataSourcePaths } : {}),
              ...this.pickPointAnnotations(usageData),
            };
            consumption.usage.push(usage);
          }

          if (consumption.spend.length > 0 || consumption.usage.length > 0) {
            const utility: Utility = {
              name: key.charAt(0).toUpperCase() + key.slice(1),
              icon: this.getUtilityIcon(key.charAt(0).toUpperCase() + key.slice(1)),
              consumption,
            };
            utilities.push(utility);
          }
        }
      }
    }
    return utilities;
  };

  /** Per-point bill annotations, carried verbatim so a period's usage and spend rows agree on them. */
  private pickPointAnnotations(point: DatedValue): Partial<DatedValue> {
    return {
      ...(point.onPeakUsage != null ? { onPeakUsage: point.onPeakUsage } : {}),
      ...(point.offPeakUsage != null ? { offPeakUsage: point.offPeakUsage } : {}),
      ...(point.avgTemp != null ? { avgTemp: point.avgTemp } : {}),
      ...(point.sourcePage != null ? { sourcePage: point.sourcePage } : {}),
      ...(point.extra_fields && Object.keys(point.extra_fields).length ? { extra_fields: point.extra_fields } : {}),
      ...(point.billFields && Object.keys(point.billFields).length ? { billFields: point.billFields } : {}),
    };
  }

  // UTILS

  // BRN-2282/2283 AC4: was a five-arm switch. `undefined` for an unrecognised name is preserved.
  getUtilityIcon = (utilityName: string): string | undefined => findUtility(utilityName)?.icon;

  /**
   * BRN-2282/2283 AC4: was a five-arm switch plus one alias. The five original units are byte-identical
   * to what it returned (`kWh`, `Gallons`, `MMBtu`, `kWh`, `Gallons`), because the one commodity
   * definition carries the same strings; `propanegas` is a legacy key the backend still emits and it
   * keeps its own answer.
   */
  getUtilityUnit = (utilityName: string): string | undefined => {
    if (utilityName.toLowerCase() === 'propanegas') {
      return UTILITY_UNIT.mmbtu;
    }
    return findUtility(utilityName)?.usageUnit;
  };
}
